import React, { useState, useMemo, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import logger from '../utils/logger';
import { 
  Phone, 
  PhoneCall, 
  Clock, 
  Star, 
  Filter,
  Search,
  Download,
  RefreshCw,
  SortAsc,
  SortDesc,
  Trash2,
  X,
  ChevronLeft,
  ChevronRight,
  Eye,
  List,
  Plus,
  FolderOpen,
  Users,
  ChevronDown,
  ArrowLeft,
  BookmarkPlus,
  Check
} from 'lucide-react';
import { useData } from '../contexts/DataContext';
import { useRBACData } from '../hooks/useRBACData';
import { useRBAC } from '../contexts/RBACContext';
import { useAuth } from '../contexts/AuthContext';
import { useUserLists } from '../hooks/useUserLists';
import { useCallListDB } from '../hooks/useCallListDB';
import { LEAD_STATUS, supabase } from '../config/supabase';

const CallList = ({ onViewLead }) => {
  // Use RBAC-filtered leads for role-based access
  const { leads } = useRBACData();
  const { canDelete, isAdminOrHigher, assignedCampaigns } = useRBAC();
  const { currentUser } = useAuth();
  const { updateLeadStatus, removeFromCallList } = useData();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('score');
  const [sortOrder, setSortOrder] = useState('desc');
  const [callStatus, setCallStatus] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const leadsPerPage = 10;
  
  // Filter states
  const [filterStepOpens, setFilterStepOpens] = useState('');
  const [filterOpenSteps, setFilterOpenSteps] = useState('');
  const [filterTotalOpens, setFilterTotalOpens] = useState('');
  const [filterCampaigns, setFilterCampaigns] = useState([]); // multi-select
  const [showCampaignDropdown, setShowCampaignDropdown] = useState(false);
  const campaignDropdownRef = useRef(null);
  
  // Called leads data for the 'Called' view
  const [calledLeadsData, setCalledLeadsData] = useState([]);

  // Selection state
  const [selectedLeads, setSelectedLeads] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);

  // Tab state: 'calllist' | 'mylists'
  const [activeTab, setActiveTab] = useState('calllist');

  // List view state
  const [viewingList, setViewingList] = useState(null);
  const [viewingListLeadEmails, setViewingListLeadEmails] = useState([]);
  const [loadingListLeads, setLoadingListLeads] = useState(false);

  // Add to List modal state
  const [showAddToListModal, setShowAddToListModal] = useState(false);
  const [addToListTab, setAddToListTab] = useState('existing');
  const [newListName, setNewListName] = useState('');
  const [newListDescription, setNewListDescription] = useState('');
  const [addToListLoading, setAddToListLoading] = useState(false);
  const [addToListSuccess, setAddToListSuccess] = useState('');

  // My Lists
  const { lists, loading: listsLoading, fetchLists, createList, addLeadsToList, getListLeads, removeLeadsFromList, deleteList, getAllUserListLeadEmails, markLeadCalled, markLeadsCalled, getCalledLeadEmails } = useUserLists();

  // Leads already in any of the current user's My Lists (hidden from Call List)
  const [myListLeadEmails, setMyListLeadEmails] = useState(new Set());

  // Called leads tracking
  const [calledLeadEmails, setCalledLeadEmails] = useState(new Set());
  const [showCalledView, setShowCalledView] = useState(false);
  const [calledPage, setCalledPage] = useState(1);

  // My List detail: selection + call_status data
  const [listDetailSelection, setListDetailSelection] = useState(new Set());
  const [listDetailSelectAll, setListDetailSelectAll] = useState(false);
  const [listLeadStatuses, setListLeadStatuses] = useState({});

  // Fetch all lead emails across user's My Lists + called leads whenever lists change
  useEffect(() => {
    const syncMyListData = async () => {
      const emails = await getAllUserListLeadEmails();
      setMyListLeadEmails(new Set(emails));
      const called = await getCalledLeadEmails();
      setCalledLeadEmails(new Set(called.map(r => r.lead_email)));
    };
    syncMyListData();
  }, [lists, getAllUserListLeadEmails, getCalledLeadEmails]);

  // ── DB-level call list (all eligibility / filtering / sorting / pagination at DB level) ──
  const {
    leads: callListLeads,
    total: totalCallListLeads,
    totalPages,
    loading: loadingCallList,
    refetch: refetchCallList,
  } = useCallListDB({
    searchTerm, sortBy, sortOrder,
    filterStepOpens, filterOpenSteps, filterTotalOpens,
    filterCampaigns,
    page: currentPage,
    pageSize: leadsPerPage,
  });

  // Fetch called leads whenever calledLeadEmails changes (for 'Called' view)
  useEffect(() => {
    const emails = Array.from(calledLeadEmails);
    if (emails.length === 0) { setCalledLeadsData([]); return; }
    supabase
      .from('leads')
      .select('*')
      .in('email', emails)
      .then(({ data }) => setCalledLeadsData(data || []));
  }, [calledLeadEmails]);

  const startIndex = (currentPage - 1) * leadsPerPage;
  const endIndex   = startIndex + leadsPerPage;

  useEffect(() => {
    setCurrentPage(1);
  }, [filterStepOpens, filterOpenSteps, filterTotalOpens, filterCampaigns, searchTerm, sortBy, sortOrder]);

  useEffect(() => {
    const handler = (e) => {
      if (campaignDropdownRef.current && !campaignDropdownRef.current.contains(e.target))
        setShowCampaignDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const uniqueCampaigns = useMemo(() =>
    [...new Set(leads.map(l => l.campaign_name).filter(Boolean))],
  [leads]);

  const clearFilters = () => {
    setFilterStepOpens(''); setFilterOpenSteps(''); setFilterTotalOpens('');
    setFilterCampaigns([]); setSearchTerm('');
  };

  const handleSelectLead = (leadId) => {
    setSelectedLeads(prev => {
      const next = new Set(prev);
      if (next.has(leadId)) next.delete(leadId); else next.add(leadId);
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectAll) { setSelectedLeads(new Set()); setSelectAll(false); }
    else { setSelectedLeads(new Set(callListLeads.map(l => l.id))); setSelectAll(true); }
  };

  const openList = async (list) => {
    setViewingList(list); setLoadingListLeads(true);
    setListDetailSelection(new Set()); setListDetailSelectAll(false);
    try {
      const rows = await getListLeads(list.id);
      setViewingListLeadEmails(rows.map(r => r.lead_email));
      // Build call_status map keyed by lead_email
      const statusMap = {};
      rows.forEach(r => { statusMap[r.lead_email] = { call_status: r.call_status || 'pending', called_at: r.called_at }; });
      setListLeadStatuses(statusMap);
    } catch (err) { setViewingListLeadEmails([]); setListLeadStatuses({}); }
    finally { setLoadingListLeads(false); }
  };

  const viewingListLeads = useMemo(() => {
    if (!viewingList) return [];
    return leads.filter(l => viewingListLeadEmails.includes(l.email));
  }, [viewingList, viewingListLeadEmails, leads]);

  const handleAddToExistingList = async (listId) => {
    const toAdd = callListLeads.filter(l => selectedLeads.has(l.id));
    if (!toAdd.length) return;
    setAddToListLoading(true);
    try {
      await addLeadsToList(listId, toAdd);
      setAddToListSuccess(`Added ${toAdd.length} lead${toAdd.length > 1 ? 's' : ''} to list.`);
      setTimeout(() => { setShowAddToListModal(false); setAddToListSuccess(''); setSelectedLeads(new Set()); setSelectAll(false); refetchCallList(); }, 1500);
    } catch (err) { logger.error(err); }
    finally { setAddToListLoading(false); }
  };

  const handleCreateAndAddToList = async () => {
    if (!newListName.trim()) return;
    const toAdd = callListLeads.filter(l => selectedLeads.has(l.id));
    setAddToListLoading(true);
    try {
      const created = await createList(newListName, newListDescription);
      if (toAdd.length) await addLeadsToList(created.id, toAdd);
      setAddToListSuccess(`List "${newListName}" created with ${toAdd.length} lead${toAdd.length !== 1 ? 's' : ''}.`);
      setNewListName(''); setNewListDescription('');
      setTimeout(() => { setShowAddToListModal(false); setAddToListSuccess(''); setSelectedLeads(new Set()); setSelectAll(false); refetchCallList(); }, 1500);
    } catch (err) { logger.error(err); }
    finally { setAddToListLoading(false); }
  };

  const handleCallStatusUpdate = async (leadId, status, notes = '') => {
    setCallStatus(prev => ({ ...prev, [leadId]: { status, notes, timestamp: new Date().toISOString() } }));
    if (status === 'interested') await updateLeadStatus(leadId, LEAD_STATUS.INTERESTED);
    else if (status === 'not_interested') await updateLeadStatus(leadId, LEAD_STATUS.ARCHIVED);
    refetchCallList();
  };

  const handleRemoveFromCallList = async (leadId) => {
    try {
      const result = await removeFromCallList(leadId);
      if (!result.success) logger.error('Failed to remove:', result.error);
      else refetchCallList();
    } catch (error) { logger.error('Error removing:', error); }
  };

  // ── My List Detail Handlers ──
  const handleListDetailSelect = (leadId) => {
    setListDetailSelection(prev => {
      const next = new Set(prev);
      if (next.has(leadId)) next.delete(leadId); else next.add(leadId);
      return next;
    });
  };

  const handleListDetailSelectAll = () => {
    if (listDetailSelectAll) { setListDetailSelection(new Set()); setListDetailSelectAll(false); }
    else { setListDetailSelection(new Set(viewingListLeads.map(l => l.id))); setListDetailSelectAll(true); }
  };

  const handleMarkCalled = async (leadEmail) => {
    if (!viewingList) return;
    try {
      await markLeadCalled(viewingList.id, leadEmail);
      setListLeadStatuses(prev => ({ ...prev, [leadEmail]: { call_status: 'called', called_at: new Date().toISOString() } }));
      // Refresh called set
      const called = await getCalledLeadEmails();
      setCalledLeadEmails(new Set(called.map(r => r.lead_email)));
    } catch (err) { logger.error('Error marking called:', err); }
  };

  const handleBulkMarkCalled = async () => {
    if (!viewingList || listDetailSelection.size === 0) return;
    const emails = viewingListLeads.filter(l => listDetailSelection.has(l.id)).map(l => l.email);
    try {
      await markLeadsCalled(viewingList.id, emails);
      const updatedStatuses = { ...listLeadStatuses };
      emails.forEach(e => { updatedStatuses[e] = { call_status: 'called', called_at: new Date().toISOString() }; });
      setListLeadStatuses(updatedStatuses);
      setListDetailSelection(new Set()); setListDetailSelectAll(false);
      const called = await getCalledLeadEmails();
      setCalledLeadEmails(new Set(called.map(r => r.lead_email)));
    } catch (err) { logger.error('Error bulk marking called:', err); }
  };

  const handleRemoveFromMyList = async (leadEmail) => {
    if (!viewingList) return;
    try {
      await removeLeadsFromList(viewingList.id, [leadEmail]);
      setViewingListLeadEmails(prev => prev.filter(e => e !== leadEmail));
      const newStatuses = { ...listLeadStatuses };
      delete newStatuses[leadEmail];
      setListLeadStatuses(newStatuses);
    } catch (err) { logger.error('Error removing from list:', err); }
  };

  const handleBulkRemoveFromMyList = async () => {
    if (!viewingList || listDetailSelection.size === 0) return;
    const emails = viewingListLeads.filter(l => listDetailSelection.has(l.id)).map(l => l.email);
    try {
      await removeLeadsFromList(viewingList.id, emails);
      setViewingListLeadEmails(prev => prev.filter(e => !emails.includes(e)));
      const newStatuses = { ...listLeadStatuses };
      emails.forEach(e => delete newStatuses[e]);
      setListLeadStatuses(newStatuses);
      setListDetailSelection(new Set()); setListDetailSelectAll(false);
    } catch (err) { logger.error('Error bulk removing from list:', err); }
  };

  const calledTotalPages = Math.ceil(calledLeadsData.length / leadsPerPage);
  const calledStartIdx = (calledPage - 1) * leadsPerPage;
  const calledEndIdx = calledStartIdx + leadsPerPage;
  const calledPaginated = calledLeadsData.slice(calledStartIdx, calledEndIdx);

  const getCallStatusColor = (status) => {
    switch (status) {
      case 'called': return 'bg-blue-100 text-blue-800';
      case 'interested': return 'bg-green-100 text-green-800';
      case 'not_interested': return 'bg-red-100 text-red-800';
      case 'callback': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const exportCallList = async () => {
    const campaigns = isAdminOrHigher() ? null : (assignedCampaigns?.length ? assignedCampaigns : []);
    const { data: result } = await supabase.rpc('get_call_list_leads', {
      p_user_email:         currentUser.email.toLowerCase(),
      p_campaigns:          campaigns,
      p_search:             searchTerm       ? searchTerm.trim()            : null,
      p_sort_by:            sortBy           || 'score',
      p_sort_order:         sortOrder        || 'desc',
      p_filter_step:        filterStepOpens  ? parseInt(filterStepOpens,  10) : null,
      p_filter_open_steps:  filterOpenSteps  ? parseInt(filterOpenSteps,  10) : null,
      p_filter_total_opens: filterTotalOpens ? parseInt(filterTotalOpens, 10) : null,
      p_filter_campaigns:   filterCampaigns?.length ? filterCampaigns : null,
      p_page:               1,
      p_page_size:          10000,
    });
    const allLeads = result?.leads || [];
    const csv = [
      ['Email','Name','Company','Score','Campaign','Last Activity'].join(','),
      ...allLeads.map(l => [l.email, l.first_name||'', l.company||'', l.score||0,
        l.campaign_name||'', l.last_event_date ? new Date(l.last_event_date).toLocaleDateString() : ''].join(','))
    ].join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `call-list-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const renderLeadRow = (lead) => {
    const lcs = callStatus[lead.id];
    const lad = {
      currentStep:  lead.current_step      || 0,
      openSteps:    lead.open_steps        || 0,
      totalOpens:   lead.total_opens       || 0,
      totalReplies: lead.valid_reply_count || 0,
    };
    const initial = (lead.first_name || lead.email).charAt(0).toUpperCase();
    return (
      <tr key={lead.id} className="hover:bg-gray-50 transition-colors border-b border-gray-200">
        <td className="px-4 py-3">
          <input type="checkbox" checked={selectedLeads.has(lead.id)}
            onChange={() => handleSelectLead(lead.id)} onClick={e => e.stopPropagation()}
            className="rounded border-gray-300 cursor-pointer" />
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center space-x-3">
            <div className="w-9 h-9 bg-blue-500 rounded-full flex items-center justify-center text-white font-medium flex-shrink-0 text-sm">{initial}</div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-gray-900">{lead.first_name || lead.email.split('@')[0]}</div>
              <div className="text-xs text-gray-500">{lead.email}</div>
              {lcs && <span className={`mt-0.5 inline-block px-2 py-0.5 text-xs rounded ${getCallStatusColor(lcs.status)}`}>{lcs.status.replace('_',' ')}</span>}
            </div>
          </div>
        </td>
        <td className="px-4 py-3 text-sm text-gray-700">{lead.campaign_name || 'N/A'}</td>
        <td className="px-4 py-3">
          <div className="flex items-center space-x-1">
            <Star className="w-4 h-4 text-orange-500" />
            <span className="text-sm font-bold text-gray-900">{lead.score || 0}</span>
          </div>
        </td>
        <td className="px-4 py-3">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${lcs ? getCallStatusColor(lcs.status) : 'bg-orange-100 text-orange-700'}`}>
            {lcs ? lcs.status.replace('_',' ') : 'call list'}
          </span>
        </td>
        <td className="px-4 py-3">
          <div className="space-y-0.5 text-xs">
            <div className="text-purple-700">Open Steps: {lad.openSteps}/5</div>
            <div className="text-green-700">Opens: {lad.totalOpens}</div>
            <div className="text-orange-700">Replies: {lad.totalReplies}</div>
          </div>
        </td>
        <td className="px-4 py-3 text-xs text-gray-500">
          {lead.last_event_date ? new Date(lead.last_event_date).toLocaleDateString() : 'No activity'}
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center space-x-1">
            <button onClick={() => onViewLead(lead)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded" title="View"><Eye className="w-4 h-4" /></button>
            {canDelete() && (
              <button onClick={() => handleRemoveFromCallList(lead.id)} className="p-1.5 text-red-500 hover:bg-red-50 rounded" title="Remove"><Trash2 className="w-4 h-4" /></button>
            )}
          </div>
        </td>
      </tr>
    );
  };

  const renderTableContainer = (rows, totalCount, pg, setPg, si, ei, tp) => (
    <>
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-auto" style={{ maxHeight: 'calc(100vh - 440px)', minHeight: '360px' }}>
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3"><input type="checkbox" checked={selectAll} onChange={handleSelectAll} className="rounded border-gray-300 cursor-pointer" /></th>
                {['Contact Info','Campaign','Score','Status','Step Info','Last Activity','Actions'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>{rows.map(lead => renderLeadRow(lead))}</tbody>
          </table>
        </div>
      </div>
      {tp > 1 && (
        <div className="flex items-center justify-between bg-white p-4 rounded-lg border border-gray-200 mt-3">
          <span className="text-sm text-gray-500">Showing {si+1}–{Math.min(ei, totalCount)} of {totalCount}</span>
          <div className="flex items-center space-x-1">
            <button onClick={() => setPg(Math.max(1, pg-1))} disabled={pg===1} className="p-2 rounded border border-gray-300 disabled:opacity-40"><ChevronLeft className="w-4 h-4" /></button>
            {Array.from({ length: Math.min(7,tp) }, (_,i) => {
              const p = tp<=7 ? i+1 : pg<=4 ? i+1 : pg>=tp-3 ? tp-6+i : pg-3+i;
              return <button key={p} onClick={() => setPg(p)} className={`px-3 py-1.5 rounded text-sm ${pg===p ? 'bg-blue-600 text-white' : 'border border-gray-300 hover:bg-gray-50'}`}>{p}</button>;
            })}
            <button onClick={() => setPg(Math.min(tp, pg+1))} disabled={pg===tp} className="p-2 rounded border border-gray-300 disabled:opacity-40"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      )}
    </>
  );

  return (
    <div className="space-y-5">
      {/* Tab Bar */}
      <div className="flex items-center border-b border-gray-200">
        <button onClick={() => { setActiveTab('calllist'); setViewingList(null); }}
          className={`px-5 py-2.5 text-sm font-medium transition-colors ${activeTab==='calllist' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}>
          <span className="flex items-center space-x-2"><Phone className="w-4 h-4" /><span>Call List</span></span>
        </button>
        <button onClick={() => setActiveTab('mylists')}
          className={`px-5 py-2.5 text-sm font-medium transition-colors ${activeTab==='mylists' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}>
          <span className="flex items-center space-x-2"><List className="w-4 h-4" /><span>My Lists</span>{lists.length > 0 && <span className="bg-blue-100 text-blue-700 text-xs px-1.5 py-0.5 rounded-full">{lists.length}</span>}</span>
        </button>
      </div>

      {/* ── CALL LIST TAB ── */}
      {activeTab === 'calllist' && (
        <div className="space-y-4">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Call List</h1>
              <p className="text-sm text-gray-500">{totalCallListLeads} qualified leads{loadingCallList && ' — syncing…'}</p>
            </div>
            <div className="flex items-center space-x-2 flex-wrap gap-y-2">
              {selectedLeads.size > 0 && (
                <button onClick={() => { setAddToListTab('existing'); setShowAddToListModal(true); }}
                  className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium">
                  <BookmarkPlus className="w-4 h-4" /><span>Add {selectedLeads.size} to List</span>
                </button>
              )}
              <button onClick={exportCallList} className="bg-blue-100 text-blue-700 px-4 py-2 rounded-lg hover:bg-blue-200 flex items-center space-x-2 text-sm font-medium">
                <Download className="w-4 h-4" /><span>Export</span>
              </button>
              <button onClick={() => refetchCallList()} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2 text-sm font-medium">
                <RefreshCw className={`w-4 h-4 ${loadingCallList ? 'animate-spin' : ''}`} /><span>Refresh</span>
              </button>
            </div>
          </div>

          {/* Search + Filters */}
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 space-y-3">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-400 w-4 h-4" />
                <input type="text" placeholder="Search leads…" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-blue-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div className="flex items-center space-x-2">
                <button onClick={() => setShowFilters(!showFilters)}
                  className={`px-3 py-2 rounded-lg flex items-center space-x-1 text-sm font-medium ${showFilters ? 'bg-blue-600 text-white' : 'bg-white text-blue-600 border border-blue-200 hover:bg-blue-50'}`}>
                  <Filter className="w-4 h-4" /><span>Filters</span>
                </button>
                <select value={sortBy} onChange={e => setSortBy(e.target.value)}
                  className="border border-blue-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="score">Score</option>
                  <option value="created_at">Date Added</option>
                  <option value="last_event_date">Last Activity</option>
                </select>
                <button onClick={() => setSortOrder(s => s==='asc'?'desc':'asc')} className="p-2 text-blue-500 hover:bg-blue-100 rounded-lg border border-blue-200 bg-white">
                  {sortOrder==='asc' ? <SortAsc className="w-4 h-4" /> : <SortDesc className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <AnimatePresence>
              {showFilters && (
                <motion.div initial={{ opacity:0, height:0 }} animate={{ opacity:1, height:'auto' }} exit={{ opacity:0, height:0 }}
                  className="grid grid-cols-2 sm:grid-cols-5 gap-3 pt-3 border-t border-blue-200">
                  <select value={filterStepOpens} onChange={e => setFilterStepOpens(e.target.value)}
                    className="border border-blue-200 rounded-lg px-3 py-2 text-sm bg-white">
                    <option value="">Any Step</option>
                    {[1,2,3,4,5].map(s => <option key={s} value={s}>Step {s}</option>)}
                  </select>
                  <select value={filterOpenSteps} onChange={e => setFilterOpenSteps(e.target.value)}
                    className="border border-blue-200 rounded-lg px-3 py-2 text-sm bg-white">
                    <option value="">Open Steps</option>
                    {[1,2,3,4,5].map(s => <option key={s} value={s}>{s}+</option>)}
                  </select>
                  <select value={filterTotalOpens} onChange={e => setFilterTotalOpens(e.target.value)}
                    className="border border-blue-200 rounded-lg px-3 py-2 text-sm bg-white">
                    <option value="">Total Opens</option>
                    {[1,3,5,10].map(s => <option key={s} value={s}>{s}+</option>)}
                  </select>
                  <div className="relative" ref={campaignDropdownRef}>
                    <button onClick={() => setShowCampaignDropdown(v => !v)}
                      className="w-full flex items-center justify-between border border-blue-200 rounded-lg px-3 py-2 text-sm bg-white">
                      <span className="truncate">{filterCampaigns.length===0 ? 'All Campaigns' : `${filterCampaigns.length} selected`}</span>
                      <ChevronDown className="w-3 h-3 ml-1 flex-shrink-0" />
                    </button>
                    {showCampaignDropdown && (
                      <div className="absolute z-20 top-full mt-1 left-0 w-64 bg-white border border-gray-200 rounded-lg shadow-lg max-h-52 overflow-y-auto">
                        {uniqueCampaigns.map(c => (
                          <label key={c} className="flex items-center space-x-2 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                            <input type="checkbox" checked={filterCampaigns.includes(c)}
                              onChange={() => setFilterCampaigns(prev => prev.includes(c) ? prev.filter(x=>x!==c) : [...prev,c])}
                              className="rounded border-gray-300" />
                            <span className="text-sm text-gray-700 truncate">{c}</span>
                          </label>
                        ))}
                        {uniqueCampaigns.length===0 && <p className="px-3 py-2 text-sm text-gray-400">No campaigns</p>}
                      </div>
                    )}
                  </div>
                  <button onClick={clearFilters} className="bg-red-50 text-red-600 border border-red-200 rounded-lg px-3 py-2 text-sm font-medium flex items-center justify-center space-x-1 hover:bg-red-100">
                    <X className="w-4 h-4" /><span>Clear</span>
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-white border border-gray-200 rounded-lg p-4 flex items-center space-x-3">
              <div className="w-9 h-9 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0"><Phone className="w-5 h-5 text-white" /></div>
              <div><p className="text-xs text-gray-500">Total Leads</p><p className="text-xl font-bold text-gray-900">{totalCallListLeads}</p></div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 flex items-center space-x-3">
              <div className="w-9 h-9 bg-green-500 rounded-lg flex items-center justify-center flex-shrink-0"><BookmarkPlus className="w-5 h-5 text-white" /></div>
              <div><p className="text-xs text-gray-500">In My Lists</p><p className="text-xl font-bold text-gray-900">{myListLeadEmails.size}</p></div>
            </div>
            <div onClick={() => { setShowCalledView(!showCalledView); setCalledPage(1); }}
              className="bg-white border border-gray-200 rounded-lg p-4 flex items-center space-x-3 cursor-pointer hover:border-blue-300 transition-colors">
              <div className="w-9 h-9 bg-indigo-500 rounded-lg flex items-center justify-center flex-shrink-0"><PhoneCall className="w-5 h-5 text-white" /></div>
              <div><p className="text-xs text-gray-500">Called</p><p className="text-xl font-bold text-gray-900">{calledLeadEmails.size}</p></div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 flex items-center space-x-3">
              <div className="w-9 h-9 bg-orange-500 rounded-lg flex items-center justify-center flex-shrink-0"><Clock className="w-5 h-5 text-white" /></div>
              <div><p className="text-xs text-gray-500">Interested</p><p className="text-xl font-bold text-gray-900">{Object.values(callStatus).filter(s=>s.status==='interested').length}</p></div>
            </div>
          </div>

          {/* Called leads view */}
          {showCalledView ? (
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <button onClick={() => setShowCalledView(false)} className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"><ArrowLeft className="w-4 h-4" /></button>
                <h2 className="text-lg font-bold text-gray-900">Called Leads</h2>
                <span className="text-sm text-gray-400">{calledLeadsData.length} leads</span>
              </div>
              {calledLeadsData.length > 0
                ? renderTableContainer(calledPaginated, calledLeadsData.length, calledPage, setCalledPage, calledStartIdx, calledEndIdx, calledTotalPages)
                : (
                  <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
                    <PhoneCall className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No leads marked as called yet.</p>
                  </div>
                )
              }
            </div>
          ) : (
          <>
          {/* Table or empty */}
          {callListLeads.length > 0
            ? renderTableContainer(callListLeads, totalCallListLeads, currentPage, setCurrentPage, startIndex, endIndex, totalPages)
            : (
              <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
                <Phone className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No leads match the current filters.</p>
              </div>
            )
          }
          </>
          )}
        </div>
      )}

      {/* ── MY LISTS TAB ── */}
      {activeTab === 'mylists' && (
        <div className="space-y-4">
          {viewingList ? (
            /* List detail view — enhanced with marking + removal */
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <button onClick={() => setViewingList(null)} className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"><ArrowLeft className="w-4 h-4" /></button>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{viewingList.name}</h2>
                  {viewingList.description && <p className="text-sm text-gray-500">{viewingList.description}</p>}
                </div>
                <span className="ml-auto text-sm text-gray-400">{viewingListLeads.length} leads</span>
              </div>

              {/* Bulk actions bar */}
              {listDetailSelection.size > 0 && (
                <div className="flex items-center space-x-2 bg-blue-50 border border-blue-200 rounded-lg px-4 py-2">
                  <span className="text-sm text-blue-700 font-medium">{listDetailSelection.size} selected</span>
                  <button onClick={handleBulkMarkCalled}
                    className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-xs font-medium hover:bg-indigo-700 flex items-center space-x-1">
                    <PhoneCall className="w-3.5 h-3.5" /><span>Mark Called</span>
                  </button>
                  <button onClick={handleBulkRemoveFromMyList}
                    className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-xs font-medium hover:bg-red-700 flex items-center space-x-1">
                    <Trash2 className="w-3.5 h-3.5" /><span>Remove from List</span>
                  </button>
                </div>
              )}

              {/* Stats mini-row */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-white border border-gray-200 rounded-lg p-3 flex items-center space-x-2">
                  <Users className="w-4 h-4 text-blue-500" />
                  <div><p className="text-xs text-gray-400">Total</p><p className="text-lg font-bold text-gray-900">{viewingListLeads.length}</p></div>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-3 flex items-center space-x-2">
                  <PhoneCall className="w-4 h-4 text-indigo-500" />
                  <div><p className="text-xs text-gray-400">Called</p><p className="text-lg font-bold text-gray-900">{Object.values(listLeadStatuses).filter(s => s.call_status === 'called').length}</p></div>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-3 flex items-center space-x-2">
                  <Clock className="w-4 h-4 text-orange-500" />
                  <div><p className="text-xs text-gray-400">Pending</p><p className="text-lg font-bold text-gray-900">{Object.values(listLeadStatuses).filter(s => s.call_status !== 'called').length}</p></div>
                </div>
              </div>

              {loadingListLeads ? (
                <div className="flex items-center justify-center py-12"><RefreshCw className="w-5 h-5 animate-spin text-blue-500 mr-2" /><span className="text-gray-500">Loading…</span></div>
              ) : viewingListLeads.length > 0 ? (
                <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                  <div className="overflow-auto" style={{ maxHeight: 'calc(100vh - 500px)', minHeight: '300px' }}>
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
                        <tr>
                          <th className="px-4 py-3"><input type="checkbox" checked={listDetailSelectAll} onChange={handleListDetailSelectAll} className="rounded border-gray-300 cursor-pointer" /></th>
                          {['Contact Info','Campaign','Score','Call Status','Last Activity','Actions'].map(h => (
                            <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {viewingListLeads.map(lead => {
                          const st = listLeadStatuses[lead.email] || { call_status: 'pending' };
                          const initial = (lead.first_name || lead.email).charAt(0).toUpperCase();
                          return (
                            <tr key={lead.id} className="hover:bg-gray-50 transition-colors border-b border-gray-200">
                              <td className="px-4 py-3">
                                <input type="checkbox" checked={listDetailSelection.has(lead.id)}
                                  onChange={() => handleListDetailSelect(lead.id)}
                                  className="rounded border-gray-300 cursor-pointer" />
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex items-center space-x-3">
                                  <div className="w-9 h-9 bg-blue-500 rounded-full flex items-center justify-center text-white font-medium flex-shrink-0 text-sm">{initial}</div>
                                  <div className="min-w-0">
                                    <div className="text-sm font-semibold text-gray-900">{lead.first_name || lead.email.split('@')[0]}</div>
                                    <div className="text-xs text-gray-500">{lead.email}</div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-700">{lead.campaign_name || 'N/A'}</td>
                              <td className="px-4 py-3">
                                <div className="flex items-center space-x-1"><Star className="w-4 h-4 text-orange-500" /><span className="text-sm font-bold text-gray-900">{lead.score || 0}</span></div>
                              </td>
                              <td className="px-4 py-3">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${st.call_status === 'called' ? 'bg-indigo-100 text-indigo-800' : 'bg-gray-100 text-gray-600'}`}>
                                  {st.call_status === 'called' ? 'Called' : 'Pending'}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-xs text-gray-500">
                                {lead.last_event_date ? new Date(lead.last_event_date).toLocaleDateString() : 'No activity'}
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex items-center space-x-1">
                                  <button onClick={() => onViewLead(lead)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded" title="View"><Eye className="w-4 h-4" /></button>
                                  {st.call_status !== 'called' && (
                                    <button onClick={() => handleMarkCalled(lead.email)} className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded" title="Mark Called"><PhoneCall className="w-4 h-4" /></button>
                                  )}
                                  <button onClick={() => handleRemoveFromMyList(lead.email)} className="p-1.5 text-red-500 hover:bg-red-50 rounded" title="Remove from List"><Trash2 className="w-4 h-4" /></button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
                  <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No leads in this list.</p>
                </div>
              )}
            </div>
          ) : (
            /* Lists grid */
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">My Lists</h2>
                <button onClick={() => { setAddToListTab('new'); setShowAddToListModal(true); }}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium">
                  <Plus className="w-4 h-4" /><span>New List</span>
                </button>
              </div>
              {listsLoading ? (
                <div className="flex items-center justify-center py-12"><RefreshCw className="w-5 h-5 animate-spin text-blue-500 mr-2" /><span className="text-gray-500">Loading lists…</span></div>
              ) : lists.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
                  <FolderOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="font-medium text-gray-700 mb-1">No lists yet</p>
                  <p className="text-sm text-gray-400">Select leads from the Call List and click "Add to List".</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {lists.map(list => (
                    <div key={list.id} className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 truncate">{list.name}</h3>
                          {list.description && <p className="text-xs text-gray-400 mt-0.5 truncate">{list.description}</p>}
                          <p className="text-xs text-gray-400 mt-1">{new Date(list.created_at).toLocaleDateString()}</p>
                        </div>
                        <button onClick={async () => { if(window.confirm(`Delete "${list.name}"?`)) await deleteList(list.id); }}
                          className="p-1.5 text-gray-300 hover:text-red-500 rounded ml-2 flex-shrink-0">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="mt-3 flex items-center justify-between">
                        <span className="text-sm font-medium text-blue-600">{list.lead_count} lead{list.lead_count!==1?'s':''}</span>
                        <button onClick={() => openList(list)} className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center space-x-1">
                          <Eye className="w-3.5 h-3.5" /><span>View</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── ADD TO LIST MODAL ── */}
      <AnimatePresence>
        {showAddToListModal && (
          <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <motion.div initial={{ scale:0.95, opacity:0 }} animate={{ scale:1, opacity:1 }} exit={{ scale:0.95, opacity:0 }}
              className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-gray-900">Add to List</h2>
                <button onClick={() => setShowAddToListModal(false)} className="p-1.5 text-gray-400 hover:text-gray-600 rounded"><X className="w-5 h-5" /></button>
              </div>

              {addToListSuccess ? (
                <div className="flex items-center space-x-2 py-6 justify-center">
                  <Check className="w-6 h-6 text-green-500" />
                  <span className="text-green-700 font-medium">{addToListSuccess}</span>
                </div>
              ) : (
                <>
                  <div className="flex border-b border-gray-200 mb-4">
                    <button onClick={() => setAddToListTab('existing')}
                      className={`px-4 py-2 text-sm font-medium ${addToListTab==='existing' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'}`}>
                      Existing List
                    </button>
                    <button onClick={() => setAddToListTab('new')}
                      className={`px-4 py-2 text-sm font-medium ${addToListTab==='new' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'}`}>
                      Create New
                    </button>
                  </div>

                  {addToListTab === 'existing' ? (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {lists.length === 0 ? (
                        <p className="text-sm text-gray-400 text-center py-4">No lists yet. Create one first.</p>
                      ) : lists.map(list => (
                        <button key={list.id} onClick={() => handleAddToExistingList(list.id)} disabled={addToListLoading}
                          className="w-full flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors disabled:opacity-50">
                          <div className="text-left">
                            <p className="text-sm font-medium text-gray-900">{list.name}</p>
                            {list.description && <p className="text-xs text-gray-400">{list.description}</p>}
                          </div>
                          <span className="text-xs text-gray-400">{list.lead_count} leads</span>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">List Name *</label>
                        <input type="text" value={newListName} onChange={e => setNewListName(e.target.value)}
                          placeholder="e.g. High Priority Q1"
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Description (optional)</label>
                        <textarea value={newListDescription} onChange={e => setNewListDescription(e.target.value)}
                          placeholder="Brief description…" rows={2}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
                      </div>
                      <button onClick={handleCreateAndAddToList} disabled={addToListLoading || !newListName.trim()}
                        className="w-full bg-blue-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center space-x-2">
                        {addToListLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                        <span>{addToListLoading ? 'Creating…' : `Create & Add ${selectedLeads.size} Lead${selectedLeads.size!==1?'s':''}`}</span>
                      </button>
                    </div>
                  )}
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CallList;

